/**
 * Serviço de consulta de CNPJ resiliente com múltiplos provedores em cascata:
 * 1. BrasilAPI (primário)
 * 2. Cnpj.ws (fallback público com CORS habilitado)
 */

export interface DadosCnpjConsulta {
  razao_social: string;
  nome_fantasia?: string;
  email?: string;
  telefone?: string;
  ddd_telefone_1?: string;
  ddd_telefone_2?: string;
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  municipio?: string;
  cidade?: string;
  uf?: string;
  estado?: string;
  opcao_pelo_simples?: boolean;
  optante_simples_nacional?: boolean;
  qsa?: Array<{
    nome_socio: string;
    qualificacao_socio?: string;
  }>;
}

export const cnpjService = {
  async buscarCnpj(cnpj: string): Promise<DadosCnpjConsulta | null> {
    const cleanCnpj = cnpj.replace(/\D/g, '');
    if (cleanCnpj.length !== 14) return null;

    // 1. Tentar BrasilAPI (timeout 3.5s)
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3500);
      const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cleanCnpj}`, {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (res.ok) {
        const data = await res.json();
        return {
          razao_social: data.razao_social || '',
          nome_fantasia: data.nome_fantasia || data.razao_social || '',
          email: data.email || '',
          telefone: data.ddd_telefone_1 || data.ddd_telefone_2 || '',
          ddd_telefone_1: data.ddd_telefone_1 || '',
          ddd_telefone_2: data.ddd_telefone_2 || '',
          cep: data.cep || '',
          logradouro: data.logradouro || '',
          numero: data.numero || '',
          complemento: data.complemento || '',
          bairro: data.bairro || '',
          municipio: data.municipio || '',
          cidade: data.municipio || '',
          uf: data.uf || '',
          estado: data.uf || '',
          opcao_pelo_simples: data.opcao_pelo_simples ?? false,
          optante_simples_nacional: data.opcao_pelo_simples ?? false,
          qsa: data.qsa || [],
        };
      }
    } catch (bErr) {
      console.warn('⚠️ [CNPJ] BrasilAPI indisponível ou instável, acionando fallback...', bErr);
    }

    // 2. Fallback: Cnpj.ws público (suporta CORS no browser)
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      const res = await fetch(`https://publica.cnpj.ws/cnpj/${cleanCnpj}`, {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (res.ok) {
        const data = await res.json();
        const est = data.estabelecimento || {};
        const optSimples = data.simples?.simples === 'Sim';
        const tel1 = est.ddd1 ? `${est.ddd1}${est.telefone1 || ''}` : '';
        const tel2 = est.ddd2 ? `${est.ddd2}${est.telefone2 || ''}` : '';

        return {
          razao_social: data.razao_social || '',
          nome_fantasia: est.nome_fantasia || data.razao_social || '',
          email: est.email || '',
          telefone: tel1 || tel2 || '',
          ddd_telefone_1: tel1,
          ddd_telefone_2: tel2,
          cep: est.cep || '',
          logradouro: `${est.tipo_logradouro ? est.tipo_logradouro + ' ' : ''}${est.logradouro || ''}`.trim(),
          numero: est.numero || '',
          complemento: est.complemento || '',
          bairro: est.bairro || '',
          municipio: est.cidade?.nome || '',
          cidade: est.cidade?.nome || '',
          uf: est.estado?.sigla || '',
          estado: est.estado?.sigla || '',
          opcao_pelo_simples: optSimples,
          optante_simples_nacional: optSimples,
          qsa: data.socios?.map((s: { nome?: string; qualificacao_socio?: { descricao?: string } }) => ({
            nome_socio: s.nome || '',
            qualificacao_socio: s.qualificacao_socio?.descricao || '',
          })) || [],
        };
      }
    } catch (wsErr) {
      console.warn('⚠️ [CNPJ] Falha também no fallback Cnpj.ws:', wsErr);
    }

    return null;
  },
};
